/**
 * Презентационные шаги/чрома ядра редактора записи (задача 77 §1.1, 89 §4.2).
 *
 * Шаги `вид → проект → элемент → часы` с `data-testid`-якорями `field-entry-flow*`
 * (регрессионный гард рабочего): `FlowHeader`, `PillStack`, `Step1WorkType`,
 * `Step2Project`, `Step3Element`, `Step4Hours`, `ActionBar`, `ConfirmDelete`,
 * `CloseConfirm`. Surface-агностичны — никакого API/offline.
 *
 * §2.1: tap-таргеты читают density-var'ы `--entry-tap-*` (мобилка — крупные из
 * :root, `desktop-dock` — компактные из `[data-entry-surface]`). Ручной ввод
 * № проекта — единый `NumericPad` на всех поверхностях (см. `ProjectSearchPanel`).
 */
import * as React from "react";
import { useTranslation } from "react-i18next";
import { AlertDialog as AlertDialogPrimitive } from "radix-ui";
import { Check, ChevronRight, Keyboard, Plus, RotateCcw, Trash2, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/templates/ConfirmDialog";
import { HoursStepper } from "@/components/field/HoursStepper";
import { RecentProjectChips } from "@/components/field/RecentProjectChips";
import { ProjectSearchPanel } from "@/components/field/ProjectSearchPanel";
import type { AssemblyElement, Project, WorkType } from "@/api/field-timesheet";
import { cn } from "@/lib/utils";
import { useReleaseDirtyState } from "@/lib/releaseDirtyState";
import { FormFieldError, type FormValidationIssue } from "@/components/ui/form-validation";

import type { PillLabels, Step } from "./types";

// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------

interface FlowHeaderProps {
  mode: "create" | "edit";
  title: string;
  stepLabel: string;
  onClose: () => void;
}

export function FlowHeader({
  mode,
  title,
  stepLabel,
  onClose,
}: FlowHeaderProps): React.ReactElement {
  const { t } = useTranslation();
  return (
    <header
      className="shrink-0"
      style={{
        background: "var(--soot-900, #08090b)",
        color: "#fff",
        borderBottom: "1px solid var(--soot-900, #08090b)",
      }}
      data-testid="field-entry-flow-header"
    >
      <div className="flex h-12 items-center gap-3 px-3">
        <button
          type="button"
          onClick={onClose}
          className="inline-flex h-9 w-9 items-center justify-center rounded-[2px] transition-colors hover:bg-white/10"
          style={{ color: "#fff" }}
          aria-label={t("field.entryFlow.closeAria")}
          data-testid="field-entry-flow-close"
        >
          <X className="h-5 w-5" aria-hidden="true" />
        </button>
        <div className="min-w-0 flex-1">
          <div
            className="font-condensed text-[10px] uppercase tracking-[0.22em]"
            style={{ color: "rgba(255,255,255,0.55)", fontFamily: "var(--font-display)" }}
            data-testid="field-entry-flow-step-counter"
          >
            {mode === "edit" ? t("field.entryFlow.editBadge") : stepLabel}
          </div>
          <div
            className="truncate font-condensed text-[14px] font-semibold uppercase tracking-[0.1em]"
            style={{ fontFamily: "var(--font-display)" }}
            data-testid="field-entry-flow-title"
          >
            {title}
          </div>
        </div>
      </div>
    </header>
  );
}

// ---------------------------------------------------------------------------
// Pills stack — зафиксированные шаги.
// ---------------------------------------------------------------------------

interface PillStackProps {
  step: Step;
  labels: PillLabels;
  /** §115/2.1 — работа по выбранной паре закрыта → бейдж «доработка» на pill «Работа». */
  reworkActive: boolean;
  onReset: (step: Step) => void;
}

export function PillStack({
  step,
  labels,
  reworkActive,
  onReset,
}: PillStackProps): React.ReactElement | null {
  const { t } = useTranslation();
  const pills: { idx: 1 | 2 | 3; titleKey: string; value: string | null }[] = [
    { idx: 1, titleKey: "field.entryFlow.pill.workType", value: labels.step1 },
    { idx: 2, titleKey: "field.entryFlow.pill.project", value: labels.step2 },
    { idx: 3, titleKey: "field.entryFlow.pill.element", value: labels.step3 },
  ];
  const visible = pills.filter((p) => p.idx < step && p.value !== null);
  if (visible.length === 0) return null;

  return (
    <div
      className="flex flex-col gap-1.5 border-b px-3 py-2"
      style={{ borderColor: "var(--rule-line)", background: "var(--bg-panel-1)" }}
      data-testid="field-entry-flow-pills"
    >
      {visible.map((p) => (
        <div
          key={p.idx}
          className="flex items-center gap-2"
          data-testid={`field-entry-flow-pill-${p.idx}`}
          // §20/8.3: каскад — небольшое смещение вправо для прогресса
          // (шаг 1 = 0, шаг 2 = 8px, шаг 3 = 16px).
          style={{ marginLeft: (p.idx - 1) * 8 }}
        >
          <Check
            // §20/8.3: muted check вместо чёрного фона-плашки — индикатор,
            // не кнопка. Раньше outlined-Check на чёрном квадрате читался
            // как «нажми меня».
            className="h-4 w-4 shrink-0"
            style={{ color: "var(--fg-3)", opacity: 0.6 }}
            aria-hidden="true"
          />
          <div
            className="flex min-w-0 flex-1 items-baseline gap-2 px-2 py-1.5"
            style={{
              background: "var(--bg-panel-0)",
              border: "1px solid var(--rule-line)",
              borderRadius: "var(--radius-button)",
              boxShadow: "var(--shadow-plate)",
            }}
          >
            <span
              className="shrink-0 font-condensed text-[10px] uppercase tracking-[0.2em]"
              style={{ color: "var(--fg-3)", fontFamily: "var(--font-display)" }}
            >
              {p.idx}. {t(p.titleKey)}
            </span>
            <span
              className="min-w-0 flex-1 truncate font-condensed text-[13px] font-semibold uppercase tracking-[0.04em]"
              style={{ color: "var(--fg-1)", fontFamily: "var(--font-display)" }}
            >
              {p.value}
            </span>
            {/* §115/2.1: бейдж «доработка» на pill «Работа» (idx 1), когда пара
                закрыта. Informational-тон (не caution) — доработка легальна. */}
            {p.idx === 1 && reworkActive && (
              <span
                className="tone-info shrink-0 font-condensed text-[9px] font-semibold uppercase tracking-[0.14em]"
                style={{ borderRadius: "var(--radius-button)", padding: "2px 6px" }}
                data-testid="field-entry-flow-pill-1-rework-badge"
              >
                {t("field.entryFlow.rework.badge")}
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={() => onReset(p.idx)}
            className="inline-flex h-9 w-9 shrink-0 items-center justify-center"
            style={{
              color: "var(--fg-3)",
              border: "1px solid var(--rule-line)",
              borderRadius: "var(--radius-button)",
              background: "var(--bg-panel-0)",
            }}
            aria-label={t("field.entryFlow.pill.clearAria")}
            data-testid={`field-entry-flow-pill-${p.idx}-clear`}
          >
            {/* §20/8.3: корзина вместо ×, очистка значения шага = семантика
                «удалить выбор», не «закрыть». */}
            <Trash2 className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 1 — work_type
// ---------------------------------------------------------------------------

interface Step1Props {
  myWorkTypes: readonly WorkType[];
  onPickStandard: (wt: WorkType) => void;
  onPickCustom: (text: string) => void;
}

export function Step1WorkType({
  myWorkTypes,
  onPickStandard,
  onPickCustom,
}: Step1Props): React.ReactElement {
  const { t } = useTranslation();
  const [customOpen, setCustomOpen] = React.useState(false);
  const [customText, setCustomText] = React.useState("");
  useReleaseDirtyState(customOpen && customText !== "");

  return (
    <div className="flex flex-col gap-2 p-3" data-testid="field-entry-flow-step1">
      {myWorkTypes.map((wt) => {
        const id = wt.id;
        if (id === null || id === undefined) return null;
        return (
          <button
            key={id}
            type="button"
            onClick={() => onPickStandard(wt)}
            data-testid={`field-entry-flow-step1-option-${id}`}
            className="flex items-center justify-between gap-3 text-left transition-[background-color,box-shadow,transform] duration-75 active:translate-y-[1px]"
            style={{
              minHeight: "var(--entry-tap-primary)",
              padding: "10px 16px",
              background: "var(--bg-panel-0)",
              border: "1px solid var(--rule-line)",
              borderRadius: "var(--radius-button)",
              boxShadow: "var(--shadow-plate)",
              color: "var(--fg-1)",
            }}
          >
            <span
              className="font-condensed font-semibold uppercase tracking-[0.06em]"
              style={{ fontFamily: "var(--font-display)", fontSize: "var(--entry-tile-font)" }}
            >
              {wt.name}
            </span>
            {/* §20/8.3: крупная жирная стрелка вместо текстового «→». */}
            <ChevronRight
              className="h-6 w-6 shrink-0"
              style={{ color: "var(--fg-2)" }}
              strokeWidth={2.5}
              aria-hidden="true"
            />
          </button>
        );
      })}

      {!customOpen ? (
        <button
          type="button"
          onClick={() => setCustomOpen(true)}
          data-testid="field-entry-flow-step1-custom"
          className="mt-2 flex items-center justify-center gap-2 transition-[background-color,box-shadow,transform] duration-75 active:translate-y-[1px]"
          style={{
            minHeight: "var(--entry-tap-secondary)",
            padding: "10px 16px",
            background: "transparent",
            border: "1px dashed var(--rule-strong, var(--rule-line))",
            borderRadius: "var(--radius-button)",
            color: "var(--fg-2)",
          }}
        >
          <Plus className="h-4 w-4" aria-hidden="true" />
          <span
            className="font-condensed text-[13px] font-semibold uppercase tracking-[0.1em]"
            style={{ fontFamily: "var(--font-display)" }}
          >
            {t("field.entryFlow.step1.customCta")}
          </span>
        </button>
      ) : (
        <div
          className="mt-2 flex flex-col gap-2 p-3"
          style={{
            background: "var(--bg-panel-1)",
            border: "1px solid var(--rule-line)",
            borderRadius: "var(--radius-button)",
          }}
          data-testid="field-entry-flow-step1-custom-form"
        >
          <label className="flex flex-col gap-1">
            <span
              className="font-condensed text-[10px] uppercase tracking-[0.2em]"
              style={{ color: "var(--fg-3)", fontFamily: "var(--font-display)" }}
            >
              {t("field.entryFlow.step1.customLabel")}
            </span>
            <input
              type="text"
              autoFocus
              value={customText}
              onChange={(e) => setCustomText(e.target.value)}
              placeholder={t("field.entryFlow.step1.customPlaceholder")}
              maxLength={120}
              data-testid="field-entry-flow-step1-custom-input"
              className="w-full px-3 py-2 text-[14px] outline-none focus:ring-2"
              style={{
                background: "var(--bg-panel-0)",
                border: "1px solid var(--rule-line)",
                borderRadius: "var(--radius-button)",
                color: "var(--fg-1)",
                ["--tw-ring-color" as string]: "var(--accent-agent)",
              }}
            />
          </label>
          <div className="flex justify-end gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setCustomOpen(false);
                setCustomText("");
              }}
              data-testid="field-entry-flow-step1-custom-cancel"
            >
              {t("field.entryFlow.cancel")}
            </Button>
            <Button
              variant="default"
              size="sm"
              disabled={customText.trim().length === 0}
              onClick={() => onPickCustom(customText.trim())}
              data-testid="field-entry-flow-step1-custom-submit"
            >
              {t("field.entryFlow.continue")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 2 — project
// ---------------------------------------------------------------------------

interface Step2Props {
  recentProjects: readonly Project[];
  onPickRecent: (id: number) => void;
  onPickHit: (
    v:
      | { kind: "id"; projectId: number; code: string | null; name: string }
      | { kind: "raw"; code: string },
  ) => void;
  onSkip: () => void;
}

export function Step2Project({
  recentProjects,
  onPickRecent,
  onPickHit,
  onSkip,
}: Step2Props): React.ReactElement {
  const { t } = useTranslation();
  const [searchOpen, setSearchOpen] = React.useState(false);

  return (
    <div className="flex h-full min-h-0 flex-col gap-3 p-3" data-testid="field-entry-flow-step2">
      {recentProjects.length > 0 && !searchOpen && (
        <div className="flex flex-col gap-1.5">
          <span
            className="px-1 font-condensed text-[10px] uppercase tracking-[0.2em]"
            style={{ color: "var(--fg-3)", fontFamily: "var(--font-display)" }}
          >
            {t("field.entryFlow.step2.recentHint")}
          </span>
          <RecentProjectChips
            projects={recentProjects}
            onPick={onPickRecent}
            data-testid="field-entry-flow-step2-recent"
          />
        </div>
      )}

      {!searchOpen ? (
        <button
          type="button"
          onClick={() => setSearchOpen(true)}
          data-testid="field-entry-flow-step2-manual"
          className="flex items-center justify-center gap-2 transition-[background-color,box-shadow,transform] duration-75 active:translate-y-[1px]"
          style={{
            minHeight: "var(--entry-tap-secondary)",
            padding: "10px 16px",
            background: "transparent",
            border: "1px dashed var(--rule-strong, var(--rule-line))",
            borderRadius: "var(--radius-button)",
            color: "var(--fg-2)",
          }}
        >
          <Keyboard className="h-4 w-4" aria-hidden="true" />
          <span
            className="font-condensed text-[13px] font-semibold uppercase tracking-[0.1em]"
            style={{ fontFamily: "var(--font-display)" }}
          >
            {t("field.entryFlow.step2.manualCta")}
          </span>
        </button>
      ) : (
        /* min-h-0 + flex-1 — даём панели всю свободную высоту, чтобы
           внутренние results могли скроллиться, а pad оставался на
           стабильной нижней позиции (см. §20/4.1). */
        <div className="flex min-h-0 flex-1 flex-col">
          <ProjectSearchPanel
            onPick={(v) => {
              onPickHit(v);
            }}
            data-testid="field-entry-flow-step2-search"
          />
        </div>
      )}

      <button
        type="button"
        onClick={onSkip}
        data-testid="field-entry-flow-step2-skip"
        className="flex items-center justify-center gap-2 transition-[background-color,box-shadow,transform] duration-75 active:translate-y-[1px]"
        style={{
          // §20/16.1: 44px = WCAG-минимум, secondary CTA — экономит
          // вертикаль рядом с sticky NumericPad на шаге проекта.
          minHeight: "var(--entry-tap-tertiary)",
          padding: "10px 16px",
          background: "transparent",
          border: "1px solid var(--rule-line)",
          borderRadius: "var(--radius-button)",
          color: "var(--fg-2)",
        }}
      >
        <span
          className="font-condensed text-[12px] uppercase tracking-[0.16em]"
          style={{ fontFamily: "var(--font-display)" }}
        >
          {t("field.entryFlow.step2.skipCta")}
        </span>
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 3 — assembly element (задача 89 §4.2)
// ---------------------------------------------------------------------------

interface Step3ElementProps {
  elements: readonly AssemblyElement[];
  loading: boolean;
  onPick: (id: number) => void;
  onSkip: () => void;
}

export function Step3Element({
  elements,
  loading,
  onPick,
  onSkip,
}: Step3ElementProps): React.ReactElement {
  const { t } = useTranslation();
  const isEmpty = !loading && elements.length === 0;

  return (
    <div className="flex flex-col gap-2 p-3" data-testid="field-entry-flow-step3">
      {loading && (
        <span
          className="px-1 font-condensed text-[10px] uppercase tracking-[0.2em]"
          style={{ color: "var(--fg-3)", fontFamily: "var(--font-display)" }}
          data-testid="field-entry-flow-step3-loading"
        >
          {t("field.entryFlow.step3.loading")}
        </span>
      )}

      {isEmpty && (
        <span
          className="px-1 font-condensed text-[10px] uppercase tracking-[0.2em]"
          style={{ color: "var(--fg-3)", fontFamily: "var(--font-display)" }}
          data-testid="field-entry-flow-step3-empty"
        >
          {t("field.entryFlow.step3.empty")}
        </span>
      )}

      {elements.map((el) => {
        const id = el.id;
        if (id === null || id === undefined) return null;
        return (
          <button
            key={id}
            type="button"
            onClick={() => onPick(id)}
            data-testid={`field-entry-flow-step3-option-${id}`}
            className="flex items-center justify-between gap-3 text-left transition-[background-color,box-shadow,transform] duration-75 active:translate-y-[1px]"
            style={{
              minHeight: "var(--entry-tap-primary)",
              padding: "10px 16px",
              background: "var(--bg-panel-0)",
              border: "1px solid var(--rule-line)",
              borderRadius: "var(--radius-button)",
              boxShadow: "var(--shadow-plate)",
              color: "var(--fg-1)",
            }}
          >
            <span
              className="font-condensed font-semibold uppercase tracking-[0.06em]"
              style={{ fontFamily: "var(--font-display)", fontSize: "var(--entry-tile-font)" }}
            >
              {el.name}
            </span>
            <ChevronRight
              className="h-6 w-6 shrink-0"
              style={{ color: "var(--fg-2)" }}
              strokeWidth={2.5}
              aria-hidden="true"
            />
          </button>
        );
      })}

      <button
        type="button"
        onClick={onSkip}
        data-testid="field-entry-flow-step3-skip"
        className="mt-2 flex items-center justify-center gap-2 transition-[background-color,box-shadow,transform] duration-75 active:translate-y-[1px]"
        style={{
          minHeight: "var(--entry-tap-tertiary)",
          padding: "10px 16px",
          background: "transparent",
          border: "1px solid var(--rule-line)",
          borderRadius: "var(--radius-button)",
          color: "var(--fg-2)",
        }}
      >
        <span
          className="font-condensed text-[12px] uppercase tracking-[0.16em]"
          style={{ fontFamily: "var(--font-display)" }}
        >
          {/* Пустой список → «Продолжить без элемента»; непустой → «Без элемента». */}
          {isEmpty ? t("field.entryFlow.step3.skipEmptyCta") : t("field.entryFlow.step3.skipCta")}
        </span>
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 4 — hours
// ---------------------------------------------------------------------------

interface Step4Props {
  value: number | null;
  onChange: (half: number) => void;
  /** §115/2.1 — пара закрыта → баннер «это доработка» + поле идентификатора. */
  reworkActive: boolean;
  /** ISO-дата закрытия работы (YYYY-MM-DD); показываем «ДД.ММ» в баннере. */
  reworkCompletedAt: string | null;
  reworkNote: string | null;
  onReworkNoteChange: (note: string) => void;
  error?: FormValidationIssue;
}

/** ISO `YYYY-MM-DD` → «ДД.ММ» без TZ-парсинга (только для показа). */
function formatDayMonth(iso: string): string {
  const parts = iso.split("-");
  if (parts.length !== 3) return iso;
  return `${parts[2]}.${parts[1]}`;
}

export function Step4Hours({
  value,
  onChange,
  reworkActive,
  reworkCompletedAt,
  reworkNote,
  onReworkNoteChange,
  error,
}: Step4Props): React.ReactElement {
  const { t } = useTranslation();
  // value хранится в `half-units`; HoursStepper работает в часах.
  const hours = value === null ? null : value / 2;
  return (
    <div className="flex flex-col gap-4 p-4" data-testid="field-entry-flow-step4">
      {reworkActive && (
        <div
          className="tone-info flex flex-col gap-2 p-3"
          style={{
            borderRadius: "var(--radius-card)",
            borderLeftWidth: "3px",
            borderLeftColor: "var(--accent-tech)",
          }}
          data-testid="field-entry-flow-rework-banner"
        >
          <div className="flex items-center gap-2">
            <RotateCcw className="h-4 w-4 shrink-0" aria-hidden="true" />
            <span
              className="font-condensed text-[11px] font-semibold uppercase tracking-[0.16em]"
              style={{ fontFamily: "var(--font-display)" }}
            >
              {t("field.entryFlow.rework.bannerTitle")}
            </span>
          </div>
          <p className="text-[13px] leading-snug">
            {reworkCompletedAt
              ? t("field.entryFlow.rework.bannerBody", {
                  date: formatDayMonth(reworkCompletedAt),
                })
              : t("field.entryFlow.rework.bannerBodyNoDate")}
          </p>
          <input
            type="text"
            value={reworkNote ?? ""}
            onChange={(e) => onReworkNoteChange(e.target.value)}
            placeholder={t("field.entryFlow.rework.notePlaceholder")}
            maxLength={200}
            aria-label={t("field.entryFlow.rework.noteLabel")}
            data-testid="field-entry-flow-rework-note"
            className="w-full px-3 py-2 text-[14px] outline-none focus:ring-2"
            style={{
              background: "var(--bg-panel-0)",
              border: "1px solid var(--rule-line)",
              borderRadius: "var(--radius-button)",
              color: "var(--fg-1)",
              ["--tw-ring-color" as string]: "var(--accent-tech)",
            }}
          />
        </div>
      )}
      <span
        className="px-1 font-condensed text-[10px] uppercase tracking-[0.2em]"
        style={{ color: "var(--fg-3)", fontFamily: "var(--font-display)" }}
      >
        {t("field.entryFlow.step4.hint")}
      </span>
      <HoursStepper
        value={hours}
        onChange={(h) => onChange(Math.round(h * 2))}
        data-testid="field-entry-flow-step4-stepper"
        aria-invalid={Boolean(error)}
        aria-describedby={error ? error.id : undefined}
        aria-label={t("field.entryFlow.step4.title")}
        // §20/8.3: «0 ч.» вместо «— ч» — прочерк рабочие читали как «4».
        placeholder="0 ч."
      />
      <FormFieldError issue={error} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sticky-bottom action bar
// ---------------------------------------------------------------------------

interface ActionBarProps {
  submitLabel: string;
  submitDisabled: boolean;
  submitting: boolean;
  onSubmit: () => void;
  showDelete: boolean;
  deleting: boolean;
  deleteTarget: string;
  onDeleteRequest: () => Promise<void>;
}

export function ActionBar({
  submitLabel,
  submitDisabled,
  submitting,
  onSubmit,
  showDelete,
  deleting,
  deleteTarget,
  onDeleteRequest,
}: ActionBarProps): React.ReactElement {
  const { t } = useTranslation();
  const [confirmDelete, setConfirmDelete] = React.useState(false);
  return (
    <div
      className="shrink-0 border-t"
      style={{
        borderColor: "var(--rule-line)",
        background: "var(--bg-panel-0)",
        paddingBottom: "max(8px, env(safe-area-inset-bottom, 8px))",
        boxShadow: "0 -8px 24px -12px rgba(0,0,0,0.08)",
      }}
      data-testid="field-entry-flow-action-bar"
    >
      <div className="flex items-center gap-2 px-3 pt-3">
        {showDelete && (
          <button
            type="button"
            disabled={deleting || submitting}
            onClick={() => setConfirmDelete(true)}
            data-testid="field-entry-flow-delete"
            aria-label={t("field.entryFlow.deleteAria")}
            className={cn(
              "inline-flex h-12 w-12 shrink-0 items-center justify-center transition-colors disabled:cursor-not-allowed disabled:opacity-40",
            )}
            style={{
              color: "#9a3412",
              border: "1px solid var(--rule-line)",
              borderRadius: "var(--radius-button)",
              background: "var(--bg-panel-0)",
            }}
          >
            <Trash2 className="h-5 w-5" aria-hidden="true" />
          </button>
        )}
        <button
          type="button"
          disabled={submitDisabled}
          onClick={onSubmit}
          data-testid="field-entry-flow-submit"
          className={cn(
            "inline-flex h-12 flex-1 items-center justify-center gap-2 transition-colors disabled:cursor-not-allowed",
          )}
          style={{
            color: submitDisabled ? "var(--fg-4)" : "#fff",
            background: submitDisabled ? "var(--bg-panel-2)" : "var(--soot-900, #08090b)",
            borderRadius: "var(--radius-button)",
            fontFamily: "var(--font-display)",
          }}
        >
          <Check className="h-4 w-4" aria-hidden="true" />
          <span
            className="font-condensed text-[14px] font-semibold uppercase tracking-[0.1em]"
            style={{ fontVariantNumeric: "tabular-nums" }}
          >
            {submitting ? t("field.entryFlow.saving") : submitLabel}
          </span>
        </button>
      </div>

      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title={t("field.entryFlow.deleteTitle")}
        body={t("field.entryFlow.deleteBodyNamed", { name: deleteTarget })}
        submitLabel={t("field.entryFlow.deleteOk")}
        onConfirm={onDeleteRequest}
        destructive
        pending={deleting}
        testId="field-entry-flow-delete-confirm"
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Confirm dialogs
// ---------------------------------------------------------------------------

interface ConfirmProps {
  open: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

export function CloseConfirm({ open, onCancel, onConfirm }: ConfirmProps): React.ReactElement {
  const { t } = useTranslation();
  return (
    <AlertDialogPrimitive.Root open={open} onOpenChange={(v) => !v && onCancel()}>
      <AlertDialogPrimitive.Portal>
        <AlertDialogPrimitive.Overlay
          className="fixed inset-0 z-50 bg-black/30"
          data-testid="field-entry-flow-close-overlay"
        />
        <AlertDialogPrimitive.Content
          className="fixed top-1/2 left-1/2 z-50 grid w-[calc(100%-2rem)] max-w-sm -translate-x-1/2 -translate-y-1/2 gap-4 p-4 outline-none"
          style={{
            background: "var(--bg-panel-0)",
            border: "1px solid var(--rule-strong, var(--rule-line))",
            borderRadius: "var(--radius-card)",
            boxShadow: "var(--shadow-plate)",
          }}
          data-testid="field-entry-flow-close-confirm"
        >
          <AlertDialogPrimitive.Title
            className="font-condensed text-[15px] font-semibold uppercase tracking-[0.06em]"
            style={{ color: "var(--fg-1)" }}
          >
            {t("field.entryFlow.closeConfirmTitle")}
          </AlertDialogPrimitive.Title>
          <AlertDialogPrimitive.Description
            className="text-[13px] leading-snug"
            style={{ color: "var(--fg-2)" }}
          >
            {t("field.entryFlow.closeConfirmBody")}
          </AlertDialogPrimitive.Description>
          <div className="flex items-center justify-end gap-2">
            <AlertDialogPrimitive.Cancel asChild>
              <Button variant="ghost" size="sm" data-testid="field-entry-flow-close-cancel">
                {t("field.entryFlow.closeConfirmCancel")}
              </Button>
            </AlertDialogPrimitive.Cancel>
            <AlertDialogPrimitive.Action asChild>
              <Button
                variant="default"
                size="sm"
                onClick={onConfirm}
                data-testid="field-entry-flow-close-ok"
              >
                {t("field.entryFlow.closeConfirmOk")}
              </Button>
            </AlertDialogPrimitive.Action>
          </div>
        </AlertDialogPrimitive.Content>
      </AlertDialogPrimitive.Portal>
    </AlertDialogPrimitive.Root>
  );
}
